import { validateLevelUpMoney } from "store/liveOps/loyalty/levels/moneyReward";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  getLevelItem,
  createLoyaltyLevel,
  updateLoyaltyLevel,
  replaceLoyaltyLevelRewards,
  getLoyaltyLevelTranslation,
  setLoyaltyLevelTranslation,
} from "store/liveOps/loyalty/levels/slice";
import { validateLoyaltyRewards } from "store/liveOps/loyalty/levels/rewards";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalDefTabs } from "configs/types";

import Button from "components/buttons/Button";
import LoyaltyProgramLevelsContentTab from "../tabs/loyaltyProgram/levels/LoyaltyProgramLevelsContentTab";

import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsLoyaltyLevelEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import LoyaltyProgramLevelsMainTab from "../tabs/loyaltyProgram/levels/LoyaltyProgramLevelsMainTab";
import LoyaltyProgramLevelsRewardsTab from "../tabs/loyaltyProgram/levels/LoyaltyProgramLevelsRewardsTab";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const LiveOpsLoyaltyLevelEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isItemFetched = useAppSelector(
    (store) => store["loyalty-levels"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["loyalty-levels"].payload);
  const locales = useAppSelector((store) => store.settings.locales);
  const translations = useAppSelector(
    (store) => store["loyalty-levels"].translations,
  );
  const isItemFetching = useAppSelector(
    (store) => store["loyalty-levels"].isItemFetching,
  );
  const formErrors = useFormErrors();
  const rewardCurrencies = useAppSelector(
    (store) => store["loyalty-levels"].rewardCurrencies,
  );
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canEdit = isEdit ? canUpdate : canCreate;

  const [tabs] = useState<ModalDefTabs[]>([
    ModalDefTabs.main,
    ModalDefTabs.rewards,
    ModalDefTabs.content,
  ]);
  const [activeTab, setActiveTab] = useState<ModalDefTabs>(ModalDefTabs.main);
  const [locale, setLocale] = useState<string>(locales[0]);
  const isLocaleLoading =
    activeTab === ModalDefTabs.content && !translations[locale];

  useEffect(() => {
    if (payload?.id) {
      dispatch(getLoyaltyLevelTranslation({ levelId: payload.id, locale }));
    }
  }, [locale, payload?.id]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [locale]);

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getLevelItem(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canEdit) return;
    if (activeTab === ModalDefTabs.main) {
      const errors = validateLevelUpMoney(
        payload?.totalLevelUpAmount,
        payload?.totalLevelUpCurrency,
        rewardCurrencies,
      );
      if (errors.length) {
        errors.forEach(({ field, code }) =>
          formErrors.setFieldError(field, code),
        );
        return;
      }
    }
    if (activeTab === ModalDefTabs.rewards && payload?.id) {
      formErrors.resetErrors();
      const validationErrors = validateLoyaltyRewards(payload.rewards ?? []);

      if (validationErrors.length > 0) {
        validationErrors.forEach(({ field, code }) =>
          formErrors.setFieldError(field, code),
        );
        dispatch(
          showNotification({
            title: t("error"),
            message: t("fixRewardValidationErrors"),
            type: NotificationTypes.error,
          }),
        );
        return;
      }

      try {
        await dispatch(
          replaceLoyaltyLevelRewards({
            levelId: payload.id,
            rewards: payload.rewards ?? [],
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (activeTab === ModalDefTabs.content && payload?.id) {
      try {
        await dispatch(
          setLoyaltyLevelTranslation({
            levelId: payload.id,
            payload: {
              locale,
              title: translations[locale]?.title ?? "",
              description: translations[locale]?.description ?? "",
              content: translations[locale]?.content ?? "",
              isActive: translations[locale]?.isActive ? 1 : 0,
            },
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });

        return;
      }

      return;
    }

    if (activeTab === ModalDefTabs.main && payload) {
      formErrors.resetErrors();

      try {
        if (searchParams.get("entityId")) {
          await dispatch(updateLoyaltyLevel(payload)).unwrap();
        } else {
          const newId = await dispatch(createLoyaltyLevel(payload)).unwrap();

          navigate(`${location.pathname}?entityId=${newId}`);
        }

        dispatch(
          showNotification({
            title: t("success"),
            message:
              searchParams.get("entityId") !== null
                ? t("entityUpdateSuccess")
                : t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });

        return;
      }
    }
  };

  const handleTabClick = (tab: ModalDefTabs) => {
    if (!isEdit && tab !== ModalDefTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    formErrors.resetErrors();
    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalDefTabs.main) {
      return (
        <LoyaltyProgramLevelsMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={!canEdit || isItemFetching}
        />
      );
    }
    if (activeTab === ModalDefTabs.content) {
      return (
        <LoyaltyProgramLevelsContentTab
          activeLocale={locale}
          setActiveLocale={setLocale}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={!canEdit}
        />
      );
    }
    if (activeTab === ModalDefTabs.rewards) {
      return (
        <LoyaltyProgramLevelsRewardsTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          isReadOnly={!canEdit}
        />
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("loyaltyLevel")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            className={styles.loyaltyTabs}
            canBeClicked={searchParams.get("entityId") !== null}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={styles.liveopsLoyaltyLevelEditModalContentWrapper}
          >
            {renderTab()}
          </Modal.Content>

          {canEdit && (
            <Modal.Footer>
              {isLocaleLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />

                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsLoyaltyLevelEditModal;
